<template>
  <div
      id="wrapper"
      :class="{'is-active': !learningStore.isDarkMode}"
      :style="wrapStyle"
      @mousemove="interactiveMethods.onMousemove"
      @mousedown="interactiveMethods.onMousemove"
      @mouseup="interactiveMethods.onMouseup"
      @touchstart="interactiveMethods.onTouchMove"
      @touchmove="interactiveMethods.onTouchMove"
      @touchend="interactiveMethods.onTouchEnd"
  >
    <div class="lectureWrap" :class="{on : learningStore.isHideBarsToggle}">
      <LazyGlobalLoading v-if="loadingStore.show"/>
      <template v-else>
        <LazyLearningHeader />
        <LazyLearningMiddle ref="middle"/>
        <LazyLearningFooter/>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  useI18n,
  useRoute,
  useRouter,
  watch,
  watchEffect
} from "#imports";
import {LazyGlobalLoading, LazyLearningFooter, LazyLearningHeader, LazyLearningMiddle} from "#components";
import {useUserStore} from "@/store/user/userStore";
import {useLearningStore} from "@/store/learning/learningStore";
import {useLearningService} from "@/composables/service/useLearningService";
import {onBeforeRouteLeave} from "vue-router";
import {useNuxtApp} from "#app";
import {useLearningWindowEventService} from "~/composables/service/useLearningWindowEventService";
import {useGenericDialog} from "~/composables/useGenericDialog";
import {RouteName} from "~~/package/enum/RouteName";
import {useLoadingStore} from "~/store/loading/loadingStore";

// TSK-11215: no sessionOwner meta here on purpose. This is not a SkillCertify assessment — the
// termination popup's copy and i18n keys (SkillCertify-duplicate-session-warning-*) are
// SkillCertify-specific, and this route has no assessmentId param, so the popup's 확인 button
// would send the user to "/" instead of back to what they were doing.

const { t } = useI18n();

const userStore = useUserStore();
const learningStore = useLearningStore();
const loadingStore = useLoadingStore();
const { openGenericAlert, openWrongApproachAlert } = useGenericDialog();
const router = useRouter();
const route = useRoute();

const middle = ref();

const learningWindowEventService = useLearningWindowEventService();
const {getLearningCourse, getTasksWithLatestCodes} = useLearningService();

const {$i18n} = useNuxtApp();
const lang = computed(() =>
    $i18n.locale.value.toUpperCase()
);

const limitPermissionLink = computed(() => `<a class="link" style="font-size: 14px" href="/home/contact">${t("common-contact-us")}</a>`)

watch(() => $i18n.locale, () => {
  initCourse();
});

onBeforeRouteLeave((to, from, next) => {
  learningStore.resetAll();
  next();
})

onBeforeMount(() => {
  loadingStore.openLoading("");
  learningStore.resetAssessment();
  learningStore.resetContestData();
  learningStore.resetLevelAssessment();
  learningStore.resetProSkillAssure();
  learningWindowEventService.addEventListener(false);
});

onMounted(() => {
  learningStore.isNeverRun = true;
  learningStore.isChangedButNotSubmit = false;
  if (userStore.isLogin()) initLearningData();
  else goToHomePage();
})

onBeforeUnmount(() => {
  learningWindowEventService.removeEventListener(false);
});

const initLearningData = () => {
  learningStore.resetInitData();
  initCourse()
      .then(goToFirstTask)
      .then((isGoToFirstTask: boolean) => {
        if (!learningStore.course.trackLevelAuthFlag){
          openNoPermissionPopup();
        }
        if (!isGoToFirstTask) {
          initTask().then((task) => {
            learningStore.updateLearningData(task);
            loadingStore.closeLoading();
          }).catch((_) => {
            handleEmptyTask()
          });
        }
      })
      .catch(error => {
        console.log(error);
        openFailPopup(error.response.data.code, error.response.data.data);
      });
}

const initCourse = async () => {
  if (
      learningStore.course.id !== parseInt(route.params.courseId as string) ||
      parseInt(route.params.taskId as string) === 0 ||
      learningStore.oldLangType !== lang.value
  ) {
    const course = await getLearningCourse(parseInt(route.params.courseId as string), lang.value);
    learningStore.updateCourse(course);
    learningStore.oldLangType = lang.value;
  }
}

const initTask = async () => {
  return await getTasksWithLatestCodes(parseInt(route.params.taskId as string), lang.value);
}

const goToFirstTask = () => {
  if(goToFirstSkillTask()) return true;
  const paramTaskId = parseInt(route.params.taskId as string)

  const taskIdLIst = learningStore.course.chapterList
      .map(chapter => chapter.skillList).flat()
      .map(skillList => skillList.taskList).flat()
      .map(task => task.id);
  const recentTaskId = learningStore.course.recentTaskId;
  const taskId = recentTaskId && taskIdLIst.includes(recentTaskId) ? learningStore.course.recentTaskId : learningStore.course.chapterList[0].skillList[0].taskList[0].id;

  if ((paramTaskId === 0 || taskId !== paramTaskId) && learningStore.taskList.filter(task => task.id === paramTaskId).length < 1) {
    router.replace({params: {...route.params, taskId}});
    return true;
  }
  return false;
}

const goToFirstSkillTask = () => {
  const paramSkillId = parseInt(route.query.skillId as string)
  if(paramSkillId) {
    const skillList =learningStore.course.chapterList
        .map(chapter => chapter.skillList).flat()
        .filter(skill => skill.id === paramSkillId);
    if(skillList.length === 1 && skillList[0].taskList.length > 0) {
      router.replace({params: {...route.params, taskId: skillList[0].taskList[0].id}});
      return true;
    }
  }
  return false;
}

const handleEmptyTask = () => {
  if (!goToFirstTask()) openFailPopup(5000);
}

const goToHomePage = () => {
  router.push({path: "/"});
}

const openNoPermissionPopup = () => {
  openGenericAlert({
    title: t("learning-limit-permission-title"),
    contents: t("learning-limit-permission-description", {link: limitPermissionLink.value}),
  }, {
    confirm: {
      title: t("common-ok"),
      event: () => router.replace({name: RouteName.Dashboard})
    },
    close: {event: () => router.replace({name: RouteName.Dashboard})}
  });
}

const openFailPopup = (errorCode: number, trackId: number) => {
  if (errorCode !== 4040)
    openWrongApproachAlert();
  else openGenericAlert({
    title: t("learning-no-access-course"),
    contents: t("learning-no-access-course-description"),
  }, {
    confirm: {
      title: t("learning-no-access-course-confirm"),
      event: () => handleWrongApproach(trackId)
    },
    close: {event: () => handleWrongApproach(trackId)}
  });
}

const handleWrongApproach = (trackId: number) => {
  if (userStore.isLogin()) router.replace({
    path: `/dashboard/skillAdvance/learn/${trackId}`
  });
  else goToHomePage();
}

watchEffect(() => {
  if (learningStore.isLastTask) openFinishedPopup();
});

const openFinishedPopup = () => {
  openGenericAlert({
    title: t("learning-finished-course"),
    contents: t("learning-finished-course-description"),
  }, {
    confirm: {
      title: t("learning-back-to-track"),
      event: () => router.push({
        path: `/dashboard/skillAdvance/learn/${learningStore.course.trackId}`
      })
    },
  });
  learningStore.isLastTask = false;
}

const interactiveMethods = {
  onMousemove(event: MouseEvent) {
    if (learningStore.draggingActivityWidget.id !== -1 || learningStore.isChangeActivityMenuHeight) {
      learningStore.mousePosition = {x: event.clientX, y: event.clientY}
      interactiveMethods.setContentPosition();
    }
  },
  onMouseup(event: MouseEvent) {
    learningStore.isChangeActivityMenuHeight = false;
    if (learningStore.draggingActivityWidget.id !== -1) {
      const isInteractionWidget = (event.target as HTMLElement).getAttribute("is-interaction-widget")
      if (
          learningStore.draggingActivityWidget.isOrderWidget
          && learningStore.draggingActivityWidget.isSelectedCard
          && !isInteractionWidget
      ) {
        const index = learningStore.task.markOrderList.findIndex(order => order.id === learningStore.draggingActivityWidget.id)
        learningStore.task.markOrderList[index].answerIndex = -1

        learningStore.isOrderMenuOpen = true;
        learningStore.isSelectedSelectionId = -1;
      }

      learningStore.draggingActivityWidget = {
        isOrderWidget: false,
        isSelectedCard: false,
        id: -1,
      }
      learningStore.mousePosition = {x: -1, y: -1}
    }
  },
  onTouchMove(event: TouchEvent) {
    if(learningStore.isChangeActivityMenuHeight) {
      if(event.touches && event.touches.length > 0) {
        learningStore.mousePosition = {x: event.touches[0].clientX, y: event.touches[0].clientY}
      }
      interactiveMethods.setContentPosition();
    }
  },
  onTouchEnd() {
    learningStore.isChangeActivityMenuHeight = false;
  },
  setContentPosition() {
    if (learningStore.isChangeActivityMenuHeight) {
      learningStore.contentPosition = {
        height: middle.value.$el.clientHeight,
        top: middle.value.$el.getBoundingClientRect().top
      }
    }
  }
}

const wrapStyle = computed(() => {
  if (learningStore.draggingActivityWidget.id !== -1) {
    return {
      userSelect: 'none',
      cursor: 'grabbing'
    }
  }
  return {}
})
</script>
